
import { useState, useEffect, useCallback, useRef } from "react";
import DeleteIcon from '@mui/icons-material/Delete';
import {
  TableHead,
  Typography,
  useMediaQuery,
  Button,
  Dialog,
  useTheme,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Box,
  Paper,
  Select,
  FormControl,
  IconButton,
  FormHelperText,
  Stack,
  Pagination,
  MenuItem,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";

import DatePickerField from "../../../common/DatePickerDashboard";
import PolicyDriverDetails from "./PolicyDriverDetails";
import { driverLicenseState, stateList } from "../../../common/StaticFieldList";
import { saveDriverAction, updatedDriverAction } from "../../../redux/actions/driverInfoActions";
import { getDetailPolicyAction,
  getDetailPolicyPaginationAction,
  deleteDriverAction
 } from "../../../redux/actions/policyActions";

const relationshipOptions = [
  "Insured",
  "Brother/Sister",
  "Child",
  "Parent",
  "Spouse",
  "Other Relative",
  "Non -Relative",
];

const driverTypeOptions = [
  "Insured",
  "Excluded",
  "Non-License: Surrendered",
  "Non-License: Never Issued",
];

const PolicyDriver = ({ policyId, setDriverId }) => {
  const dispatch = useDispatch();


  const policyDetail = useSelector((state) => state.policy.allDetailPolicy);
  const driverRowsFromRedux = policyDetail?.drivers?.content || [];
  const driversPagination = policyDetail?.drivers || null;

  const rows = policyDetail?.drivers?.content ?? [];
  const totalElements = policyDetail?.drivers?.totalElements ?? 0;
  const backendTotalPages = policyDetail?.drivers?.totalPages ?? 0;
  const backendPage0 = policyDetail?.drivers?.number ?? 0; // 0-based from server
  const currentPage1 = backendPage0 + 1;                   // 1-based for MUI Pagination
  const backendSize = policyDetail?.drivers?.size ?? 5;
  const [drivers, setDrivers] = useState(driverRowsFromRedux);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [deleteDriverId, setDeleteDriverId] = useState(null);
  const [page, setPage] = useState(0);
  const [open, setOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [driverModel, setDriverModel] = useState(false);
  const [selectedState, setSelectedState] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [id, setId] = useState(null);
  const [driverDetailRow, setDriverDetailRow] = useState(null);
  const [errors, setErrors] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const getInitialDriverState = (isFirstDriver = false) => ({
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    gender: "",
    relationshipToPolicyholder: isFirstDriver ? "Insured" : "",
    driversLicenseState: "",
    driversLicenseNumber: "",
    studentType: "Not a Student",
    maritalStatus: "",
    dateLicensed: "",
    driverType: isFirstDriver ? "Insured" : "",
    policyId

  });

  const [newDriver, setNewDriver] = useState(getInitialDriverState((driverRowsFromRedux || []).length === 0));

  useEffect(() => {
    setDrivers(Array.isArray(driverRowsFromRedux) ? driverRowsFromRedux : []);
  }, [driverRowsFromRedux]);


  useEffect(() => {
  if (!policyId) return;
  // Ask backend for drivers page 0 with current rowsPerPage
  dispatch(getDetailPolicyPaginationAction(policyId, { section: 'driver', page: 0, size: rowsPerPage }));
}, [policyId, rowsPerPage, dispatch]);


  const toBackendDate = (dateStr) => {

    if (!dateStr) return "";
    if (typeof dateStr?.format === "function") return dateStr.format("YYYY-MM-DD");
    if (typeof dateStr === "string") {
      const parts = dateStr.split("-");
      if (parts.length === 3) {
        const [a, b, c] = parts;
        if (c.length === 4) return `${c}-${a.padStart(2, "0")}-${b.padStart(2, "0")}`;
      }
      return dateStr;
    }
    return "";
  };

  const fromBackendDate = (dateStr) => {
    if (!dateStr || typeof dateStr !== "string") return "";
    const parts = dateStr.split("-");
    if (parts.length === 3) {
      const [yyyy, mm, dd] = parts;
      if (yyyy.length === 4) return `${mm}-${dd}-${yyyy}`;
    }
    return dateStr;
  };


  const handleDateChange = useCallback((date, fieldName) => {
    const formatted = date && dayjs(date).isValid() ? dayjs(date).format("YYYY-MM-DD") : "";
    setNewDriver((prev) => ({ ...prev, [fieldName]: formatted }));
    if (date && dayjs(date).isValid()) setErrors((prev) => ({ ...prev, [fieldName]: "" }));
  }, []);

  const handleCustomDateChange = (fieldName, value) => {
    setNewDriver((prev) => ({ ...prev, [fieldName]: value }));
    if (value && String(value).trim()) {
      setErrors((prev) => ({ ...prev, [fieldName]: "" }));
      setFieldErrors((prev) => ({ ...prev, [fieldName]: "" }));
    }
  };

  const handleBlur = (fieldName, errorMessage) => {
    setTouchedFields((prev) => ({ ...prev, [fieldName]: true }));
    setFieldErrors((prev) => ({ ...prev, [fieldName]: errorMessage }));
    setErrors((prev) => ({ ...prev, [fieldName]: errorMessage || "" }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "driverType") {
      if (value === "Insured") {
        setNewDriver((prev) => ({ ...prev, [name]: value, relationshipToPolicyholder: "Insured" }));
      } else {
        setNewDriver((prev) => ({ ...prev, [name]: value }));
      }
    } else {
      setNewDriver((prev) => ({ ...prev, [name]: value }));
    }

    if (String(value).trim()) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const nd = newDriver;
    if (!nd) return false;
    const temp = {};

    const licenseDate = dayjs(nd.dateLicensed);

    if (!nd.firstName?.trim()) temp.firstName = "First Name is required";
    if (!nd.lastName?.trim()) temp.lastName = "Last Name is required";
    if (!nd.dateOfBirth?.trim()) temp.dateOfBirth = "Date of Birth is required";
    if (!nd.gender?.trim()) temp.gender = "Gender is required";
    if (!nd.relationshipToPolicyholder?.trim()) temp.relationshipToPolicyholder = "Relationship is required";
    if (!nd.driversLicenseState?.trim()) temp.driversLicenseState = "License State is required";
    if (!nd.driverType?.trim()) temp.driverType = "Driver Type is required";

    if (!nd.dateLicensed?.trim()) {
      temp.dateLicensed = "License Date is required";
    } else if (!licenseDate.isValid()) {
      temp.dateLicensed = "Invalid date format";
    } else {
      const dob = dayjs(toBackendDate(nd.dateOfBirth));
      if (dob.isValid() && licenseDate.diff(dob, "year") < 16) {
        temp.dateLicensed = "You must be at least 16 years old to get a license";
      }
    }

    if (!nd.maritalStatus?.trim()) temp.maritalStatus = "Marital Status is required";
    if (!nd.studentType?.trim()) temp.studentType = "Student Type is required";
    if (!nd.driversLicenseNumber?.trim()) temp.driversLicenseNumber = "Driver License Number is required";

    setErrors(temp);
    setFieldErrors(temp);
    setTouchedFields(Object.keys(temp).reduce((acc, k) => ({ ...acc, [k]: true }), {}));

    return Object.keys(temp).length === 0;
  };

  const resetFormState = () => {
    const isFirstDriver = (drivers || []).length === 0;
    setNewDriver(getInitialDriverState(isFirstDriver));
    setErrors({});
    setFieldErrors({});
    setTouchedFields({});
  };

  const handleEditClick = (driver) => {
    setId(driver.id);
    setNewDriver({
      ...getInitialDriverState(),
      ...driver,
      dateOfBirth: fromBackendDate(driver.dateOfBirth),
      dateLicensed: fromBackendDate(driver.dateLicensed),
    });
    setIsEditing(true);
    setOpen(true);
    setErrors({});
    setFieldErrors({});
    setTouchedFields({});
  };

  const handleCancel = () => {
    resetFormState();
    setIsEditing(false);
    setOpen(false);
  };

  
  const handleCancelDelete = () => {
    setIsDialogOpen(false);
  };

  const handleOpenDialog = () => {
    if (!isEditing) {
      const isFirstDriver = (drivers || []).length === 0;
      setNewDriver(getInitialDriverState(isFirstDriver));
    }
    setOpen(true);
  };

  const afterDriverSaved = async () => {
    if (!policyId) return;
    const page0 = policyDetail?.drivers?.number ?? 0;
    await dispatch(getDetailPolicyPaginationAction(policyId, { section: 'driver', page: page0, size: rowsPerPage }));
  };


  const callbackfn = (response) => {

    if (!isEditing) setDriverId?.(response?.id);
  };

  const handleAddDriver = async () => {
    if (!validate()) return;

    const payload = {
      ...newDriver,
      dateOfBirth: toBackendDate(newDriver.dateOfBirth),
      dateLicensed: toBackendDate(newDriver.dateLicensed),
      policyId,
    };

    try {
      if (isEditing) {
        await dispatch(updatedDriverAction({ ...payload, id }, callbackfn));
      } else {
        await dispatch(saveDriverAction(payload, callbackfn));
      }
      await afterDriverSaved();


      setOpen(false);
      setIsEditing(false);
      resetFormState();
    } catch (e) {
      console.error("Failed to save driver", e);
    }
  };

  const handleChangePage = async (_e, newPage1) => {
    const page0 = newPage1 - 1;
    await dispatch(getDetailPolicyPaginationAction(policyId, { section: 'driver', page: page0, size: rowsPerPage }));
  };

  const handleChangeRowsPerPage = async (e) => {
    const newSize = parseInt(e.target.value, 10);
    setRowsPerPage(newSize); // useEffect above will refetch page 0
  };


  const handleDriverId = (driverId) => {
    const selected = (rows || []).find((d) => d.id === driverId);
    if (selected) {
      setDriverDetailRow(selected);
      setDriverModel(true);
    }
  };

  const paginatedRows = (drivers || []).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const getStateName = (code) => {
    if (!code) return "-";
    const state = stateList.find((s) => s.value === code);
    return state ? state.label : code;
  };

  const handleDeleteClick = async (id) => {    
    await setDeleteDriverId(id);
    await setIsDialogOpen(true);     
  };

  const confirmDeleteDriver = async () => {
    await dispatch(deleteDriverAction(deleteDriverId));
    await dispatch(getDetailPolicyPaginationAction(policyId, { section: 'driver', page: 0, size: rowsPerPage }));
    await setIsDialogOpen(false);  
  }

  return (
    <div>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow className="styled-table-row">
              <TableCell className="styled-table-heading" sx={{ fontWeight: "bold" }}>
                <Typography variant="subtitle1" color="black">
                  First Name
                </Typography>
              </TableCell>
              <TableCell className="styled-table-heading" sx={{ fontWeight: "bold" }}>
                <Typography variant="subtitle1" color="black">
                  Last Name
                </Typography>
              </TableCell>
              <TableCell className="styled-table-heading" sx={{ fontWeight: "bold" }}>
                <Typography variant="subtitle1" color="black">
                  Date of Birth
                </Typography>
              </TableCell>
              <TableCell className="styled-table-heading" sx={{ fontWeight: "bold" }}>
                <Typography variant="subtitle1" color="black">
                  Gender
                </Typography>
              </TableCell>
              <TableCell className="styled-table-heading" sx={{ fontWeight: "bold" }}>
                <Typography variant="subtitle1" color="black">
                  Marital Status
                </Typography>
              </TableCell>
              <TableCell className="styled-table-heading" sx={{ fontWeight: "bold" }}>
                <Typography variant="subtitle1" color="black">
                  Relationship
                </Typography>
              </TableCell>
              <TableCell className="styled-table-heading" sx={{ fontWeight: "bold" }}>
                <Typography variant="subtitle1" color="black">
                  Driver Type
                </Typography>
              </TableCell>
              <TableCell className="styled-table-heading" sx={{ fontWeight: "bold" }}>
                <Typography variant="subtitle1" color="black">
                  License State
                </Typography>
              </TableCell>
              <TableCell className="styled-table-heading" sx={{ fontWeight: "bold" }}>
                <Typography variant="subtitle1" color="black">
                  License No.
                </Typography>
              </TableCell>
              <TableCell className="styled-table-heading" sx={{ fontWeight: "bold" }}>
                <Typography variant="subtitle1" color="black">
                  Details
                </Typography>
              </TableCell>
              <TableCell className="styled-table-heading" sx={{ fontWeight: "bold" }}>
                <Typography variant="subtitle1" color="black">
                  Action
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((driver) => (
              <TableRow className="styled-table-row" key={driver.id}>
                <TableCell>{driver.firstName}</TableCell>
                <TableCell>{driver.lastName}</TableCell>
                <TableCell>{driver.dateOfBirth ? dayjs(driver.dateOfBirth).format("MM-DD-YYYY") : ""}</TableCell>
                <TableCell>{driver.gender}</TableCell>
                <TableCell>{driver.maritalStatus}</TableCell>
                <TableCell>{driver.relationshipToPolicyholder}</TableCell>
                <TableCell>{driver.driverType || "N/A"}</TableCell>
                <TableCell>{getStateName(driver.driversLicenseState)}</TableCell>
                <TableCell>{driver.driversLicenseNumber}</TableCell>
                <TableCell>
                  <Button onClick={() => handleDriverId(driver.id)}>Detail</Button>
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleEditClick(driver)} size="small" title="Edit">
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    aria-label="Delete"
                  >
                    <DeleteIcon onClick={() => handleDeleteClick(driver.id)} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} align="center">No drivers found.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {driverDetailRow && (
          <PolicyDriverDetails driver_id={driverDetailRow} driverModel={driverModel} setDriverModel={setDriverModel} />
        )}
      </TableContainer>

      {totalElements > 0 && (
        <Box sx={{ width: "100%", overflow: "hidden", padding: "16px 0" }}>
          <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2}>
            <Box display="flex" alignItems="center">
              <Typography variant="body2" sx={{ mr: 1 }}>Rows per page:</Typography>
              <Select
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
                size="small"
                sx={{ height: '36px', minWidth: '80px', '& .MuiSelect-select': { p: '8px 32px 8px 12px' } }}
              >
                {[5, 10, 15, 25, 50, 100].map((n) => (
                  <MenuItem key={n} value={n}>{n}</MenuItem>
                ))}
              </Select>
            </Box>

            <Pagination
              // Prefer backend totalPages; fallback to computed in case backend still returns wrong size
              count={Math.max(backendTotalPages, Math.ceil(totalElements / rowsPerPage))}
              page={currentPage1}
              onChange={handleChangePage}
              showFirstButton
              showLastButton
              siblingCount={1}
              boundaryCount={1}
              size="small"
            />
          </Stack>
        </Box>
      )}


      <Box sx={{ display: "flex", justifyContent: "end" }}>
        <Button
          variant="outlined"
          onClick={() => {
            setIsEditing(false);
            handleOpenDialog();
          }}
        >
          Add Driver
        </Button>
      </Box>

      <Dialog 
        open={open}
        onClose={(event, reason) => {
          // Prevent closing on backdrop click or pressing Escape
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          setOpen(false);
        }}
        disableScrollLock
        keepMounted={false}
        hideBackdrop={false}
        style={{ zIndex: 1300 }}
        >
        <DialogTitle>{isEditing ? "Edit Driver" : "Add Driver"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <label className="fnt-we">First Name</label>
              <TextField
                name="firstName"
                value={newDriver.firstName}
                onChange={(e) => {
                  const value = e.target.value;
                  const regex = /^[a-zA-Z0-9 ]*$/;
                  if (regex.test(value) && value.length <= 50) {
                    setNewDriver((prev) => ({ ...prev, firstName: value }));
                    setErrors((prev) => ({ ...prev, firstName: "" }));
                  }
                }}
                onBlur={() => {
                  if (!newDriver.firstName?.trim()) {
                    setErrors((prev) => ({ ...prev, firstName: "First Name is required" }));
                  }
                }}
                error={!!errors.firstName}
                helperText={errors.firstName || " "}
                fullWidth
                FormHelperTextProps={{ sx: { color: "red", fontSize: "0.75rem", marginTop: "3px" } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <label className="fnt-we">Last Name</label>
              <TextField
                name="lastName"
                value={newDriver.lastName}
                onChange={(e) => {
                  const value = e.target.value;
                  const regex = /^[a-zA-Z0-9 ]*$/;
                  if (regex.test(value) && value.length <= 50) {
                    setNewDriver((prev) => ({ ...prev, lastName: value }));
                    setErrors((prev) => ({ ...prev, lastName: "" }));
                  }
                }}
                onBlur={() => {
                  if (!newDriver.lastName?.trim()) {
                    setErrors((prev) => ({ ...prev, lastName: "Last Name is required" }));
                  }
                }}
                error={!!errors.lastName}
                helperText={errors.lastName || " "}
                fullWidth
                FormHelperTextProps={{ sx: { color: "red", fontSize: "0.75rem", marginTop: "3px" } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <label className="fnt-we">Date of Birth</label>
              <DatePickerField
                fieldName="dateOfBirth"
                formData={newDriver}
                handleDateChange={handleCustomDateChange}
                handleBlur={handleBlur}
                touchedFields={touchedFields}
                fieldErrors={fieldErrors}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <label className="fnt-we">Gender</label>
              <FormControl fullWidth error={!!errors.gender}>
                <Select
                  native
                  name="gender"
                  value={newDriver.gender}
                  onChange={handleChange}
                  onBlur={() => {
                    if (!newDriver.gender?.trim()) setErrors((prev) => ({ ...prev, gender: "Gender is required" }));
                  }}
                  inputProps={{ name: "gender", id: "gender-native" }}
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </Select>
                <FormHelperText sx={{ color: "red", fontSize: "0.75rem" }}>{errors.gender || " "}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <label className="fnt-we">Marital Status</label>
              <FormControl fullWidth error={!!errors.maritalStatus}>
                <Select
                  native
                  name="maritalStatus"
                  value={newDriver.maritalStatus}
                  onChange={handleChange}
                  onBlur={() => {
                    if (!newDriver.maritalStatus?.trim())
                      setErrors((prev) => ({ ...prev, maritalStatus: "Marital Status is required" }));
                  }}
                  inputProps={{ name: "maritalStatus", id: "marital-status-native" }}
                >
                  <option value="">Select Marital Status</option>
                  <option value="Single">Single</option>
                  <option value="Married">Married</option>
                  <option value="Divorced">Divorced</option>
                  <option value="Widowed">Widowed</option>
                </Select>
                <FormHelperText sx={{ color: "red", fontSize: "0.75rem" }}>{errors.maritalStatus || " "}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <label className="fnt-we">Driver Type</label>
              <FormControl fullWidth error={!!errors.driverType}>
                <Select
                  native
                  name="driverType"
                  value={newDriver.driverType}
                  onChange={handleChange}
                  onBlur={() => {
                    if (!newDriver.driverType?.trim()) setErrors((prev) => ({ ...prev, driverType: "Driver Type is required" }));
                  }}
                  inputProps={{ name: "driverType", id: "driver-type" }}
                >
                  <option value="">Select Driver Type</option>
                  {driverTypeOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Select>
                <FormHelperText sx={{ color: "red", fontSize: "0.75rem" }}>{errors.driverType || " "}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <label className="fnt-we">Relationship to Policyholder</label>
              <FormControl fullWidth error={!!errors.relationshipToPolicyholder}>
                <Select
                  native
                  name="relationshipToPolicyholder"
                  value={newDriver.relationshipToPolicyholder}
                  onChange={handleChange}
                  disabled={newDriver.driverType === "Insured"}
                  onBlur={() => {
                    if (!newDriver.relationshipToPolicyholder?.trim())
                      setErrors((prev) => ({ ...prev, relationshipToPolicyholder: "Relationship is required" }));
                  }}
                  inputProps={{ name: "relationshipToPolicyholder", id: "relationship-to-policyholder-native" }}
                >
                  <option value="">Select Relationship</option>
                  {relationshipOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Select>
                <FormHelperText sx={{ color: "red", fontSize: "0.75rem" }}>
                  {errors.relationshipToPolicyholder || " "}
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <label className="fnt-we">Student Type</label>
              <FormControl fullWidth error={!!errors.studentType}>
                <Select
                  native
                  name="studentType"
                  value={newDriver.studentType}
                  onChange={handleChange}
                  onBlur={() => {
                    if (!newDriver.studentType?.trim()) setErrors((prev) => ({ ...prev, studentType: "Student Type is required" }));
                  }}
                  inputProps={{ name: "studentType", id: "student-type-native" }}
                >
                  <option value="">Select Student Type</option>
                  <option value="Full-Time">Full-Time</option>
                  <option value="Part-Time">Part-Time</option>
                  <option value="Not a Student">Not a Student</option>
                </Select>
                <FormHelperText sx={{ color: "red", fontSize: "0.75rem" }}>{errors.studentType || " "}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <label className="fnt-we">Licensed Date</label>
              <DatePickerField
                fieldName="dateLicensed"
                formData={newDriver}
                handleDateChange={handleCustomDateChange}
                handleBlur={handleBlur}
                touchedFields={touchedFields}
                fieldErrors={fieldErrors}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <label className="fnt-we">Driver License Number</label>
              <TextField
                name="driversLicenseNumber"
                value={newDriver.driversLicenseNumber}
                onChange={(e) => {
                  const { value } = e.target;
                  if (value.length <= 18) {
                    setNewDriver((prev) => ({ ...prev, driversLicenseNumber: value.toUpperCase() }));
                    setErrors((prev) => ({ ...prev, driversLicenseNumber: "" }));
                  }
                }}
                onBlur={() => {
                  if (!newDriver.driversLicenseNumber?.trim()) {
                    setErrors((prev) => ({ ...prev, driversLicenseNumber: "Driver License Number is required" }));
                  } else if (newDriver.driversLicenseNumber.length > 18) {
                    setErrors((prev) => ({
                      ...prev,
                      driversLicenseNumber: "License number should not be more than 18 characters",
                    }));
                  }
                }}
                error={!!errors.driversLicenseNumber}
                helperText={errors.driversLicenseNumber || " "}
                fullWidth
                FormHelperTextProps={{ sx: { color: "red", fontSize: "0.75rem", marginTop: "3px" } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <label className="fnt-we">Driver License State</label>
              <FormControl fullWidth error={!!errors.driversLicenseState}>
                <Select
                  native
                  name="driversLicenseState"
                  value={newDriver.driversLicenseState}
                  onChange={(event) => {
                    const selectedValue = event.target.value;
                    setNewDriver((prev) => ({ ...prev, driversLicenseState: selectedValue }));
                    const st = driverLicenseState.find((s) => s.value === selectedValue);
                    setSelectedState(st || null);
                    if (selectedValue?.trim()) setErrors((prev) => ({ ...prev, driversLicenseState: "" }));
                  }}
                  onBlur={() => {
                    if (!newDriver.driversLicenseState?.trim()) {
                      setErrors((prev) => ({ ...prev, driversLicenseState: "License State is required" }));
                    }
                  }}
                  inputProps={{ name: "driversLicenseState", id: "drivers-license-state-native" }}
                >
                  <option value="">Select State</option>
                  {driverLicenseState.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
                <FormHelperText sx={{ color: "red", fontSize: "0.75rem" }}>
                  {errors.driversLicenseState || " "}
                </FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCancel} variant='outlined' size='large'>
            Cancel
          </Button>
          <Button onClick={handleAddDriver} variant='outlined' size='large'>
            {isEditing ? "Save Changes" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isDialogOpen}
        onClose={(event, reason) => {
            if (reason === "backdropClick" || reason === "escapeKeyDown") {
              return;
            }
            handleCancelDelete(event, reason);
          }}
        maxWidth="sm"
        fullWidth={isMobile}
        PaperProps={{
          sx: {
            margin: isMobile ? 1 : 3,
            width: isMobile ? 'calc(100% - 16px)' : 'auto'
          }
        }}
      >
        <DialogTitle
        >
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{
            marginTop: '15px',
            fontSize: isMobile ? '14px' : '16px',
            
          }}>
            You are about to <strong>permanently delete</strong> this driver's information. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{
          padding: isMobile ? '12px 16px' : '16px 24px',
          gap: 1
        }}>
          <Button
            onClick={handleCancelDelete}
            variant='outlined'
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteDriver}
            variant='outlined' color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PolicyDriver;

